import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { parseFile, ParsedData } from './services/csvParser';
import { geocodeAddress } from './services/geocoding';
import { generateRoutePDF } from './services/pdfService';
import TableControls from './components/TableControls';
import AddressTable from './components/AddressTable';
import MapControls from './components/MapControls';
import MapComponent from './components/MapComponent';
import TabDelimitedModal from './components/TabDelimitedModal';
import { Address, StartingPoint, RouteData } from './types/index';
import ResizableLayout from './components/ResizableLayout';

function App() {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isTabModalOpen, setIsTabModalOpen] = useState(false);
  const [startingPoint, setStartingPoint] = useState<StartingPoint>({
    address: '6691 Frontier Dr, Springfield, VA 22150',
    isValid: false
  });
  const [isGeocodingStart, setIsGeocodingStart] = useState(false);
  const [isCreatingRoute, setIsCreatingRoute] = useState(false);
  const [hasRoute, setHasRoute] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<any | null>(null);
  const [mapKey, setMapKey] = useState(0); // Force map re-render when needed
  const [showStatsToast, setShowStatsToast] = useState(false);
  const [stats, setStats] = useState({ total: 0, geocoded: 0, errors: 0 });
  const [shouldRedrawMap, setShouldRedrawMap] = useState(false);

  // Memoized computed values to prevent unnecessary re-renders
  const selectedAddresses = useMemo(() => 
    addresses.filter(addr => addr.isSelected), 
    [addresses]
  );

  const hasAddresses = useMemo(() => 
    addresses.length > 0, 
    [addresses.length]
  );

  const hasSelectedPoints = useMemo(() => 
    selectedAddresses.length > 0, 
    [selectedAddresses.length]
  );

  // File upload handler - Updated to use the new parseFile function
  const handleFileUpload = useCallback(async (file: File) => {
    try {
      const parsedData: ParsedData = await parseFile(file);
      setAddresses(parsedData.addresses);
      
      // Show import statistics
      const stats = parsedData.stats;
      const message = `✅ Successfully imported ${stats.total} addresses!\n\n` +
                     `📊 Import Statistics:\n` +
                     `• Total imported: ${stats.total}\n` +
                     `• With coordinates: ${stats.withCoordinates}\n` +
                     `• Without coordinates: ${stats.withoutCoordinates}\n` +
                     `• Rows ignored: ${stats.ignored}`;
      alert(message);
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error reading file. Please ensure it\'s a valid CSV or TXT file.');
    }
  }, []);

  // Clear table handler
  const handleClearTable = useCallback(() => {
    setAddresses([]);
    setCurrentRoute(null);
    setHasRoute(false);
  }, []);

  // Update address handler
  const handleUpdateAddress = useCallback((id: string, updates: Partial<Address>) => {
    setAddresses(prev => prev.map(addr => 
      addr.id === id ? { ...addr, ...updates } : addr
    ));
  }, []);

  // Delete address handler
  const handleDeleteAddress = useCallback((id: string) => {
    setAddresses(prev => prev.filter(addr => addr.id !== id));
  }, []);

  // Geocode single address
  const handleGeocodeAddress = useCallback(async (id: string) => {
    const address = addresses.find(addr => addr.id === id);
    if (!address) return;

    setIsGeocoding(true);
    try {
      const result = await geocodeAddress(address.address);
      if (result) {
        handleUpdateAddress(id, {
          latitude: result.lat,
          longitude: result.lng
        });
      } else {
        alert(`Could not geocode address: ${address.address}`);
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      alert('Error geocoding address. Please try again.');
    } finally {
      setIsGeocoding(false);
    }
  }, [addresses, handleUpdateAddress]);

  // Geocode all addresses
  const handleGeocodeAll = useCallback(async () => {
    const ungeocodedAddresses = addresses.filter(addr => 
      !addr.latitude || !addr.longitude
    );

    if (ungeocodedAddresses.length === 0) {
      alert('All addresses are already geocoded.');
      return;
    }

    setIsGeocoding(true);
    let successCount = 0;

    for (const address of ungeocodedAddresses) {
      try {
        const result = await geocodeAddress(address.address);
        if (result) {
          handleUpdateAddress(address.id, {
            latitude: result.lat,
            longitude: result.lng
          });
          successCount++;
        }
        // Rate limiting for free geocoding service
        await new Promise(resolve => setTimeout(resolve, 1200));
      } catch (error) {
        console.error(`Error geocoding ${address.address}:`, error);
      }
    }

    setIsGeocoding(false);
    alert(`Successfully geocoded ${successCount} out of ${ungeocodedAddresses.length} addresses.`);
  }, [addresses, handleUpdateAddress]);

  // Handle tab delimited input - Updated to use the new parser
  const handleTabDelimitedSubmit = useCallback((text: string) => {
    try {
      // Create a temporary file-like object for the parser
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], 'temp.txt', { type: 'text/plain' });
      
      parseFile(file).then((parsedData: ParsedData) => {
        setAddresses(prev => [...prev, ...parsedData.addresses]);
        setIsTabModalOpen(false);
        
        // Show import statistics
        const stats = parsedData.stats;
        const message = `✅ Successfully imported ${stats.total} addresses!\n\n` +
                       `📊 Import Statistics:\n` +
                       `• Total imported: ${stats.total}\n` +
                       `• With coordinates: ${stats.withCoordinates}\n` +
                       `• Without coordinates: ${stats.withoutCoordinates}`;
        alert(message);
      });
    } catch (error) {
      console.error('Error parsing tab delimited data:', error);
      alert('Error parsing data. Please check the format.');
    }
  }, []);

  // Handle address click from map
  const handleAddressClick = useCallback((address: Address) => {
    handleUpdateAddress(address.id, { 
      isSelected: !address.isSelected 
    });
  }, [handleUpdateAddress]);

  // Handle polygon completion
  const handlePolygonComplete = useCallback((selectedAddresses: Address[]) => {
    const selectedIds = new Set(selectedAddresses.map(addr => addr.id));
    setAddresses(prev => prev.map(addr => ({
      ...addr,
      isSelected: selectedIds.has(addr.id)
    })));
  }, []);

  // Starting point handlers
  const handleStartingAddressChange = useCallback((address: string) => {
    setStartingPoint(prev => ({ ...prev, address, isValid: false }));
  }, []);

  const handleGeocodeStartingPoint = useCallback(async () => {
    if (!startingPoint.address.trim()) return;
    
    setIsGeocodingStart(true);
    try {
      const result = await geocodeAddress(startingPoint.address);
      if (result) {
        setStartingPoint(prev => ({ 
          ...prev, 
          latitude: result.lat,
          longitude: result.lng,
          isValid: true 
        }));
      } else {
        alert('Could not geocode starting point address.');
      }
    } catch (error) {
      console.error('Error geocoding starting point:', error);
      alert('Error geocoding starting point.');
    } finally {
      setIsGeocodingStart(false);
    }
  }, [startingPoint.address]);

  const handleClearStartingPoint = useCallback(() => {
    setStartingPoint({ address: '', isValid: false });
  }, []);

  const handleCreateRoute = useCallback(async () => {
    const validAddresses = selectedAddresses.filter(addr => 
      addr.latitude && addr.longitude
    );
    
    if (!startingPoint.isValid) {
      alert('Please set and geocode a valid starting point.');
      return;
    }
    
    if (validAddresses.length === 0) {
      alert('No valid addresses selected for routing.');
      return;
    }

    setIsCreatingRoute(true);
    setMapKey(prev => prev + 1);
  }, [selectedAddresses, startingPoint.isValid]);

  const handleRouteCreated = useCallback((route: any) => {
    setCurrentRoute(route);
    setHasRoute(true);
    setIsCreatingRoute(false);
  }, []);

  const handleClearRoute = useCallback(() => {
    setCurrentRoute(null);
    setHasRoute(false);
    setMapKey(prev => prev + 1);
  }, []);

  const handleClearPoints = useCallback(() => {
    setAddresses(prev => prev.map(addr => ({ ...addr, isSelected: false })));
  }, []);

  const handleDownloadRoute = useCallback(async () => {
    if (!currentRoute) return;
    
    const routeData: RouteData = {
      selectedAddresses: selectedAddresses,
      startingPoint: startingPoint,
      route: currentRoute,
      totalDistance: currentRoute.distance ? `${(currentRoute.distance / 1000).toFixed(2)} km` : '',
      totalDuration: currentRoute.duration ? `${Math.round(currentRoute.duration / 60)} min` : ''
    };
    
    try {
      await generateRoutePDF(routeData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating route PDF.');
    }
  }, [currentRoute, selectedAddresses, startingPoint]);

  // Handle layout resize to trigger map redraw
  const handleLayoutResize = useCallback(() => {
    setShouldRedrawMap(true);
    // Reset the flag after a short delay to allow the effect to trigger
    setTimeout(() => setShouldRedrawMap(false), 200);
  }, []);

  // Auto-detect coordinates visibility
  useEffect(() => {
    const hasCoordinates = addresses.some(addr => addr.latitude && addr.longitude);
    setShowCoordinates(hasCoordinates);
  }, [addresses]);

  // Auto-geocode default starting address on mount
  useEffect(() => {
    if (startingPoint.address && !startingPoint.isValid) {
      (async () => {
        try {
          const result = await geocodeAddress(startingPoint.address);
          if (result) {
            setStartingPoint(prev => ({
              ...prev,
              latitude: result.lat,
              longitude: result.lng,
              isValid: true
            }));
          }
        } catch (error) {
          // Optionally handle error
        }
      })();
    }
  }, [startingPoint.address, startingPoint.isValid]);

  // Show toast with table statistics when addresses change and there is at least one address
  useEffect(() => {
    const total = addresses.length;
    const geocoded = addresses.filter(addr => addr.latitude && addr.longitude).length;
    const errors = addresses.filter(addr => addr.address && (!addr.latitude || !addr.longitude)).length;
    setStats({ total, geocoded, errors });
    if (total > 0) {
      setShowStatsToast(true);
      const timeout = setTimeout(() => setShowStatsToast(false), 3000);
      return () => clearTimeout(timeout);
    } else {
      setShowStatsToast(false);
    }
  }, [addresses]);

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-gray-900">
            Address Mapping & Route Planner
          </h1>
        </div>
      </header>

      {/* Toast for table statistics */}
      {showStatsToast && (
        <div className="fixed top-4 right-4 z-50 bg-white border border-gray-300 shadow-lg rounded-lg px-6 py-3 flex items-center space-x-6 animate-fade-in">
          <div className="flex items-center space-x-1">
            <span className="font-medium text-gray-700 text-xs">Total:</span>
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold min-w-[1.5rem] text-center">{stats.total}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="font-medium text-gray-700 text-xs">Geocoded:</span>
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold min-w-[1.5rem] text-center">{stats.geocoded}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="font-medium text-gray-700 text-xs">Errors:</span>
            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold min-w-[1.5rem] text-center">{stats.errors}</span>
          </div>
        </div>
      )}

      <ResizableLayout
        initialLeftWidth={60}
        minLeftWidth={30}
        maxLeftWidth={80}
        onResize={handleLayoutResize}
        leftPanel={
          <>
            <TableControls
              onFileUpload={handleFileUpload}
              onClearTable={handleClearTable}
              onGeocodeAll={handleGeocodeAll}
              onToggleCoordinates={() => setShowCoordinates(!showCoordinates)}
              onAddTabDelimited={() => setIsTabModalOpen(true)}
              showCoordinates={showCoordinates}
              isGeocoding={isGeocoding}
              hasAddresses={hasAddresses}
              addresses={addresses}
            />
            
            <AddressTable
              addresses={addresses}
              onUpdateAddress={handleUpdateAddress}
              onDeleteAddress={handleDeleteAddress}
              onGeocodeAddress={handleGeocodeAddress}
              showCoordinates={showCoordinates}
              isGeocoding={isGeocoding}
            />
          </>
        }
        rightPanel={
          <>
            <MapControls
              startingAddress={startingPoint.address}
              onStartingAddressChange={handleStartingAddressChange}
              onGeocodeStartingPoint={handleGeocodeStartingPoint}
              onClearStartingPoint={handleClearStartingPoint}
              onCreateRoute={handleCreateRoute}
              onClearRoute={handleClearRoute}
              onClearPoints={handleClearPoints}
              onDownloadRoute={handleDownloadRoute}
              isStartingPointValid={startingPoint.isValid}
              hasSelectedPoints={hasSelectedPoints}
              hasRoute={hasRoute}
              isCreatingRoute={isCreatingRoute}
              isGeocodingStart={isGeocodingStart}
            />
            
            <div className="flex-1 min-h-0">
              <MapComponent
                key={mapKey}
                addresses={addresses}
                onAddressClick={handleAddressClick}
                onPolygonComplete={handlePolygonComplete}
                startingPoint={startingPoint}
                onRouteCreated={handleRouteCreated}
                shouldCreateRoute={isCreatingRoute}
                onRouteCreationComplete={() => setIsCreatingRoute(false)}
                onClearRoute={() => {}}
                onClearPoints={() => {}}
                shouldClearRoute={false}
                shouldClearPoints={false}
                shouldRedrawMap={shouldRedrawMap}
              />
            </div>
          </>
        }
      />

      <TabDelimitedModal
        isOpen={isTabModalOpen}
        onClose={() => setIsTabModalOpen(false)}
        onSubmit={handleTabDelimitedSubmit}
      />
    </div>
  );
}

export default App;